// ===========================================================================
// QUÉ DICE UN VOUCHER, EN UN SOLO SITIO.
//
// Contabilidad pidió ver siempre los mismos datos del voucher —importe, fecha,
// tipo de captura, banco, cuenta de destino— «y si el voucher no tiene,
// mostrar “-”», en la revisión guiada Y en el detalle del movimiento.
//
// Estaban en dos sitios distintos y por eso divergían: la revisión enseñaba el
// medio, la hora y quién pagó, y la ficha no; la ficha enseñaba un «banco de
// destino» que salía de una columna casi vacía. Cada pantalla armaba su lista
// a mano.
//
// Aquí vive la lista. Las pantallas siguen teniendo su propia caja —la de la
// revisión deja editar y la de la ficha no—, pero las filas y el «—» los dice
// esta función. Si mañana hay un campo más, entra una vez.
// ===========================================================================

export type DatosDelVoucher = {
  importe: number | null;
  moneda: "PEN" | "USD" | null;
  /** La fecha del pago, ISO. */
  cuando: string | null;
  /** La hora impresa en el papel, «HH:MM». */
  hora: string | null;
  operacion: string | null;
  /** Cómo se pagó: Yape, Plin, transferencia, depósito… */
  medio: string | null;
  /** Cómo llegó la imagen: captura de pantalla o foto tomada. */
  origen: string | null;
  /** De qué banco salió el papel. No es lo mismo que el medio. */
  banco: string | null;
  /** Quién pagó, que casi nunca es el cliente de la boleta. */
  pagador: string | null;
  /** La cuenta de destino tal como se leyó. */
  cuentaDestino: string | null;
  /** Esa cuenta resuelta contra el maestro. `null` = no es del grupo. */
  cuentaRegistrada: {
    banco: string;
    numero: string;
    moneda: "PEN" | "USD";
    razonSocial: string | null;
  } | null;
};

const RAYA = "—";

function dinero(centimos: number, moneda: "PEN" | "USD"): string {
  const signo = moneda === "USD" ? "US$" : "S/";
  return `${signo} ${(centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fecha(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}

/**
 * Las filas del voucher, en el orden en que se leen y con «—» donde el papel
 * no dice nada.
 *
 * El «—» es deliberado y lo pidió el área: una fila ausente se confunde con un
 * dato que nadie miró, y una fila con raya dice «esto se buscó y no está».
 */
export function filasDelVoucher(v: DatosDelVoucher): Array<[string, string]> {
  return [
    [
      "Importe",
      v.importe === null ? RAYA : dinero(v.importe, v.moneda ?? "PEN"),
    ],
    [
      "Cuándo",
      v.cuando
        ? v.hora
          ? `${fecha(v.cuando)} · ${v.hora}`
          : fecha(v.cuando)
        : RAYA,
    ],
    ["N.º operación", v.operacion ?? RAYA],
    ["Cómo se pagó", v.medio ?? RAYA],
    // Banco y medio son dos ejes distintos y el pedido los juntaba: un Yape se
    // hace DESDE el BCP. Van en dos filas, no en una.
    ["Banco del voucher", v.banco ?? RAYA],
    ["Tipo de imagen", v.origen ?? RAYA],
    ["Lo pagó", v.pagador ?? RAYA],
    ["Cuenta de destino", textoDeLaCuenta(v)],
  ];
}

/**
 * Lo que hace falta para hablar de la cuenta de destino.
 *
 * Se pide esto y no el voucher entero porque la ficha del movimiento llama a
 * `comoEstaLaCuenta` con su propio tipo de candidato, que nombra la fecha
 * `fecha` y no `cuando`. Una función que exige campos que no lee obliga a
 * inventarlos en el llamador.
 */
export type CuentaDelVoucher = Pick<
  DatosDelVoucher,
  "cuentaDestino" | "cuentaRegistrada"
>;

/**
 * El número de la cuenta de destino, DICIENDO de dónde sale.
 *
 * La fila se llama «cuenta de destino» y hasta ahora enseñaba, sin avisar, el
 * número del MAESTRO cuando el papel no traía ninguno. Con un voucher del BCP
 * que dice `****1164`, la pantalla mostraba `001101090100122211` —la cuenta
 * que el sistema dedujo— y no había forma de saber de dónde salía ese número:
 * contabilidad lo buscó en el papel y no estaba.
 *
 * Sigue mostrándose, porque enseñar «—» teniendo la cuenta identificada
 * también engaña. Lo que cambia es que ahora dice que es deducida.
 */
function textoDeLaCuenta(v: CuentaDelVoucher): string {
  if (v.cuentaDestino) return v.cuentaDestino;
  if (v.cuentaRegistrada)
    return `${v.cuentaRegistrada.numero} · deducida, no está en el papel`;
  return RAYA;
}

/**
 * Si esa cuenta es del grupo o no, dicho como una frase.
 *
 * Es lo que pidió contabilidad —«mostrarse como información adicional si está
 * registrado o no está registrado en la empresa»— y ya estaba calculado en la
 * base: de 92 cuentas leídas, 65 resuelven contra el maestro y 27 no.
 *
 * `null` cuando no hay nada que decir: sin cuenta leída no se puede afirmar ni
 * que esté registrada ni que no.
 */
export function comoEstaLaCuenta(
  v: CuentaDelVoucher,
): { texto: string; tono: "ok" | "warn" } | null {
  if (v.cuentaRegistrada) {
    const c = v.cuentaRegistrada;
    return {
      texto: c.razonSocial
        ? `Cuenta ${c.banco} de ${c.razonSocial}`
        : `Cuenta ${c.banco} del grupo`,
      tono: "ok",
    };
  }
  if (v.cuentaDestino) {
    return {
      texto: "Esta cuenta no está registrada en el grupo",
      tono: "warn",
    };
  }
  return null;
}
